import { useLingui } from '@lingui/react/macro';

import ProductSearchSelect from '@/components/ProductSearchSelect';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import { useUpdateArticle } from '@/models/article';
import { ProductFromGetMany, useCreateProduct } from '@/models/product';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

import { useProductAndUniqueArticleInputs } from './useProductAndUniqueArticleInputs';

const ProductInfo = ({ row = true, showReference }: { row?: boolean; showReference?: boolean }) => {
  const { t } = useLingui();

  const { request, article, errors, view } = useArticleContext();
  const { productInput, errorMessage } = useProductAndUniqueArticleInputs();

  const { mutate: updateArticle } = useUpdateArticle({
    articleId: article.id,
    requestId: request.id,
  });

  const { mutate: createProduct } = useCreateProduct(request.id);

  const updateProduct = (product: ProductFromGetMany | null) => {
    if (product === null && !!article.product) {
      updateArticle({
        data: {
          productId: null,
        },
        optimisticData: {
          product: null,
          productAttributeValues: [],
          data: {},
        },
      });
    } else if (product && article.product?.externalId !== product.externalId) {
      updateArticle({
        data: {
          productId: product.id,
        },
        optimisticData: {
          product: {
            ...product,
            productAttributeValues: [],
            brand: null,
          },
          data: {},
        },
      });
    }
  };

  const handleCreateProduct = (externalId: string) => {
    createProduct({
      articleId: article.id,
      externalId,
    });
  };

  const canCreateProduct = view.details.product.canCreate;

  if (!productInput.shown) {
    return null;
  }

  return (
    <Stack row={row} mobileColumn gap="1rem" style={{ flex: 1, maxWidth: '100%' }}>
      <ProductSearchSelect
        article={article}
        label={t({ id: 'article.form.product.label', message: 'Product' })}
        showOptionalLabel={productInput.optional && !productInput.disabled}
        value={article.product}
        onChange={(product) => updateProduct(product ?? null)}
        onCreateProduct={canCreateProduct ? handleCreateProduct : undefined}
        error={errors.details?.product && !productInput.optional ? errorMessage : undefined}
        isDisabled={productInput.disabled}
      />

      {showReference && (
        <InputText
          label={t({ id: 'article.form.reference.label', message: 'Product reference' })}
          value={article.product?.data.reference ?? '-'}
          isDisabled
          style={{ flex: 1 }}
        />
      )}
    </Stack>
  );
};

export default ProductInfo;
